import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { ArrowDownIcon, PanelRightIcon } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { HistoryMatch, SendMessageResponse, SessionDetail } from "../../../../shared/api.ts";
import { ClassificationTab } from "@/components/panel/classification-tab.tsx";
import { RightPanel, type PanelTab } from "@/components/panel/right-panel.tsx";
import { TurnCard } from "@/components/turn/turn-card.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Segmented } from "@/components/ui/segmented.tsx";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { errorMessage, useIsMobile, useIsWide } from "@/lib/hooks.ts";
import { HttpError } from "@/lib/http.ts";
import { useModelAccess } from "@/lib/queries.ts";
import { useTurnStream } from "@/lib/turn-stream.ts";
import { buildTranscript, isActiveStatus, pendingHistoryMatch, type HistoryMatchMessage } from "@/lib/turns.ts";
import { readStorage, writeStorage } from "@/lib/utils.ts";
import { Composer, type ComposerBlock } from "./composer.tsx";
import { ClassifyEmptyState } from "./empty-state.tsx";
import { ModelAccessNotice } from "./model-access-notice.tsx";
import { AssistantBubble, HistoryMatchCard, NoticeLine, ReuseCard, UserBubble } from "./messages.tsx";
import { SessionHeader } from "./session-header.tsx";

type Segment = "chat" | "classification" | "sources";
const PANEL_KEY = "box33.panel";

/** Applies `SendMessageResponse` to the cached session before the refetch arrives. */
export function applySendResponse(detail: SessionDetail, response: SendMessageResponse): SessionDetail {
  const messages = [...detail.messages, response.message];
  if (response.historyMatch) messages.push(response.historyMatch);
  const turns = response.turn ? [...detail.turns.filter((turn) => turn.id !== response.turn?.id), response.turn] : detail.turns;
  return {
    ...detail,
    messages,
    turns,
    activeTurnId: response.turn ? response.turn.id : detail.activeTurnId,
    session: { ...detail.session, running: response.turn ? true : detail.session.running, updatedAt: response.message.createdAt },
  };
}

/** Keeps the transcript pinned to the bottom while the user has not scrolled up. */
function useStickToBottom(signature: string) {
  const ref = useRef<HTMLDivElement>(null);
  const stuck = useRef(true);
  const [away, setAway] = useState(false);

  const onScroll = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    stuck.current = distance < 96;
    setAway(distance > 320);
  }, []);

  useLayoutEffect(() => {
    const node = ref.current;
    if (node && stuck.current) node.scrollTop = node.scrollHeight;
  }, [signature]);

  const jump = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    stuck.current = true;
    node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
  }, []);

  return { ref, onScroll, away, jump };
}

/**
 * A session: header, transcript of messages and turn cards, composer, and the right panel.
 * Follows the active turn over SSE. `readOnly` is the admin view.
 */
export function SessionView({
  detail,
  queryKey,
  readOnly = false,
  linkPrefix = "/app/s/",
  headerLeading,
}: {
  detail: SessionDetail;
  queryKey: QueryKey;
  readOnly?: boolean;
  linkPrefix?: string;
  headerLeading?: ReactNode;
}) {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isWide = useIsWide();
  const isMobile = useIsMobile();
  const sessionId = detail.session.id;
  const access = useModelAccess(!readOnly);

  const [draft, setDraft] = useState("");
  const [focusKey, setFocusKey] = useState(0);
  const [segment, setSegment] = useState<Segment>("chat");
  const [panelTab, setPanelTab] = useState<PanelTab>("classification");
  const [panelOpen, setPanelOpen] = useState(() => readStorage(PANEL_KEY) !== "closed");
  const [sheetOpen, setSheetOpen] = useState(false);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
  }, [queryClient, queryKey]);

  useTurnStream(queryKey, readOnly ? null : detail.activeTurnId, refresh);

  const items = useMemo(() => buildTranscript(detail), [detail]);
  const activeTurn = detail.turns.find((turn) => turn.id === detail.activeTurnId) ?? null;
  const awaiting = pendingHistoryMatch(detail);

  const signature = useMemo(() => {
    const last = detail.turns[detail.turns.length - 1];
    return [items.length, last?.id, last?.steps.length, last?.status, last?.replyDraft?.length ?? 0, segment].join(":");
  }, [items.length, detail.turns, segment]);
  const scroll = useStickToBottom(signature);

  const send = useMutation({
    mutationFn: (body: { text: string; attachmentIds: string[] }) =>
      api.sendMessage(sessionId, {
        text: body.text,
        language,
        attachmentIds: body.attachmentIds.length > 0 ? body.attachmentIds : undefined,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData<SessionDetail>(queryKey, (current) => (current ? applySendResponse(current, response) : current));
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    },
    // Access can go while the page is open; the notice then shows on the next read.
    onError: (error) => {
      if (error instanceof HttpError && error.code === "no_model_access") void queryClient.invalidateQueries({ queryKey: queryKeys.modelAccess });
    },
  });

  const analyze = useMutation({
    mutationFn: (message: HistoryMatchMessage) => api.analyze(sessionId, { messageId: message.forMessageId, language }),
    onSuccess: () => {
      toast.success(t.messages.analysisStarted);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const reuse = useMutation({
    mutationFn: ({ match, message }: { match: HistoryMatch; message: HistoryMatchMessage }) =>
      api.reuse(sessionId, { fromSessionId: match.sessionId, matchMessageId: message.id }),
    onSuccess: (updated) => {
      if (updated && typeof updated === "object" && "session" in updated) queryClient.setQueryData(queryKey, updated);
      toast.success(t.messages.reuseDone);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const retry = useMutation({
    mutationFn: (turnId: string) => api.retryTurn(turnId),
    onSuccess: () => {
      toast.success(t.turn.retryStarted);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const stop = useMutation({
    mutationFn: (turnId: string) => api.cancelTurn(turnId),
    onSuccess: () => toast.message(t.turn.stopRequested),
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const noAccess = access.data?.effective === null;
  const block: ComposerBlock = readOnly
    ? "readOnly"
    : activeTurn && isActiveStatus(activeTurn.status)
      ? activeTurn.status === "queued"
        ? "queued"
        : "running"
      : awaiting
        ? "awaitingChoice"
        : noAccess
          ? "noAccess"
          : null;

  // On a phone the Sources segment holds the Sources, Images, and Files tabs.
  const showImages = () => {
    setPanelTab("images");
    setSegment("sources");
  };

  const togglePanel = () => {
    if (isWide) {
      setPanelOpen((open) => {
        writeStorage(PANEL_KEY, open ? "closed" : null);
        return !open;
      });
    } else {
      setSheetOpen((open) => !open);
    }
  };

  const turnActions = {
    onRetry: (turnId: string) => retry.mutate(turnId),
    onStop: (turnId: string) => stop.mutate(turnId),
    retrying: retry.isPending,
    stopping: stop.isPending,
  };

  const transcript = (
    <div className="relative min-h-0 flex-1">
      <div ref={scroll.ref} onScroll={scroll.onScroll} className="h-full overflow-y-auto overscroll-contain" data-testid="transcript">
        {items.length === 0 ? (
          <ClassifyEmptyState
            onExample={(text) => {
              setDraft(text);
              setFocusKey((key) => key + 1);
            }}
          />
        ) : (
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-3 pt-6 pb-8 sm:px-6">
            {items.map((item) => {
              switch (item.type) {
                case "user":
                  return <UserBubble key={item.key} message={item.message} own={!readOnly} />;
                case "turn":
                  return (
                    <TurnCard key={item.key} turn={item.turn} replyText={item.reply?.text ?? null} actions={turnActions} readOnly={readOnly} />
                  );
                case "assistant":
                  return <AssistantBubble key={item.key} message={item.message} />;
                case "history":
                  return (
                    <HistoryMatchCard
                      key={item.key}
                      message={item.message}
                      readOnly={readOnly}
                      busy={analyze.isPending || reuse.isPending}
                      onOpen={(id) => void navigate(`${linkPrefix}${id}`)}
                      onUse={(match, message) => reuse.mutate({ match, message })}
                      onAnalyze={(message) => analyze.mutate(message)}
                    />
                  );
                case "reuse":
                  return <ReuseCard key={item.key} message={item.message} linkPrefix={linkPrefix} />;
                case "notice":
                  return <NoticeLine key={item.key} message={item.message} />;
              }
            })}
          </div>
        )}
      </div>
      {scroll.away ? (
        <Button
          variant="outline"
          size="sm"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-card shadow-md"
          onClick={scroll.jump}
        >
          <ArrowDownIcon aria-hidden />
          {t.session.jumpToLatest}
        </Button>
      ) : null}
    </div>
  );

  const composer = !readOnly ? (
    <div className="shrink-0 bg-gradient-to-t from-background via-background to-background/0 px-3 pt-1 pb-3 sm:px-6 sm:pb-4">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-2">
        {noAccess && access.data ? <ModelAccessNotice view={access.data} /> : null}
        <Composer
          key={sessionId}
          value={draft}
          onChange={setDraft}
          ensureSession={async () => sessionId}
          onSend={async (text, attachmentIds) => {
            await send.mutateAsync({ text, attachmentIds });
            setDraft("");
          }}
          block={block}
          onStop={activeTurn ? () => stop.mutate(activeTurn.id) : undefined}
          stopping={stop.isPending}
          placeholder={detail.messages.length > 0 ? t.composer.placeholderFollowUp : t.composer.placeholder}
          autoFocus={!isMobile && detail.messages.length === 0}
          focusKey={focusKey}
        />
      </div>
    </div>
  ) : null;

  const panel = (
    <RightPanel detail={detail} readOnly={readOnly} tab={panelTab} onTabChange={setPanelTab} />
  );

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <SessionHeader
          detail={detail}
          readOnly={readOnly}
          panelOpen={isWide ? panelOpen : sheetOpen}
          onTogglePanel={togglePanel}
          showPanelToggle={!isMobile}
          leading={headerLeading}
        />
        {isMobile ? (
          <div className="shrink-0 border-b bg-background px-3 py-2">
            <Segmented
              value={segment}
              onValueChange={setSegment}
              label={t.session.segments.label}
              className="flex w-full"
              options={[
                { value: "chat", label: t.session.segments.chat },
                { value: "classification", label: t.session.segments.classification },
                { value: "sources", label: t.session.segments.sources },
              ]}
            />
          </div>
        ) : null}
        {!isMobile || segment === "chat" ? (
          <>
            {transcript}
            {composer}
          </>
        ) : segment === "classification" ? (
          <div className="min-h-0 flex-1 overflow-y-auto bg-panel">
            <ClassificationTab detail={detail} readOnly={readOnly} onShowImages={showImages} />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col bg-panel">
            <RightPanel
              detail={detail}
              readOnly={readOnly}
              tab={panelTab === "classification" ? "sources" : panelTab}
              onTabChange={setPanelTab}
              tabs={["sources", "images", "files"]}
            />
          </div>
        )}
      </div>

      {isWide && panelOpen ? (
        <aside aria-label={t.session.panel} className="flex w-[400px] shrink-0 flex-col border-l bg-panel">
          {panel}
        </aside>
      ) : null}

      {!isWide && !isMobile ? (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="right" closeLabel={t.common.close} className="bg-panel p-0">
            <div className="flex h-14 shrink-0 items-center gap-2 border-b px-4 pr-12">
              <PanelRightIcon className="size-4 text-muted-foreground" aria-hidden />
              <SheetTitle>{t.session.panel}</SheetTitle>
            </div>
            {panel}
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );
}
